import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Star, Zap } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { fmtTRY, effectivePrice, discountPercent } from "../lib/format";
import api from "../lib/api";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fav, setFav] = useState(false);
  const pct = discountPercent(product);

  useEffect(() => {
    if (!user) return;
    api.get("/favorites").then(({ data }) => {
      if (data.some((f) => f.product_id === product.product_id)) setFav(true);
    }).catch(() => {});
  }, [user, product.product_id]);

  const toggleFav = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Favorilere eklemek için giriş yapın"); return; }
    if (fav) { await api.delete(`/favorites/${product.product_id}`); setFav(false); }
    else { await api.post(`/favorites/${product.product_id}`); setFav(true); toast.success("Favorilere eklendi"); }
  };

  return (
    <div data-testid={`product-card-${product.sku}`} className="group bg-white rounded-2xl border border-stone-200/80 overflow-hidden shadow-[0_4px_20px_-4px_rgba(27,67,50,0.08)] hover:shadow-[0_12px_32px_-8px_rgba(27,67,50,0.18)] hover:-translate-y-1 transition-all duration-300">
      <Link to={`/urun/${product.slug}`} className="block relative aspect-square overflow-hidden bg-cream-dark">
        {product.images?.[0] ? (
          <img src={product.images[0]} alt={product.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-cream-dark" aria-hidden="true" />
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {pct > 0 && <span className="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider bg-terra text-white">%{pct} İndirim</span>}
          {product.is_new && <span className="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider bg-gold text-stone-900">Yeni</span>}
        </div>
        <button data-testid={`favorite-btn-${product.sku}`} onClick={toggleFav} aria-label="Favorilere ekle" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow hover:scale-110 transition-transform">
          <Heart className={`w-4 h-4 ${fav ? "fill-terra text-terra" : "text-stone-500"}`} />
        </button>
      </Link>
      <div className="p-4">
        <p className="text-[11px] uppercase tracking-widest text-stone-400 font-medium">{product.brand}</p>
        <Link to={`/urun/${product.slug}`}>
          <h3 className="font-semibold text-stone-900 text-sm sm:text-base leading-snug mt-1 line-clamp-2 min-h-[2.5rem]">{product.name}</h3>
        </Link>
        <div className="flex items-center gap-1 mt-1.5">
          {[1,2,3,4,5].map((s) => (
            <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(product.rating || 0) ? "fill-gold text-gold" : "text-stone-300"}`} />
          ))}
          <span className="text-xs text-stone-400 ml-1">({product.review_count || 0})</span>
        </div>
        <div className="flex items-end justify-between mt-3">
          <div>
            {pct > 0 && <p className="text-xs text-stone-400 line-through">{fmtTRY(product.price)}</p>}
            <p data-testid={`product-price-${product.sku}`} className="text-lg font-bold text-forest">{fmtTRY(effectivePrice(product))}{product.sale_mode === "acik" && <span className="text-xs font-normal text-stone-400">/kg</span>}</p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <button
              data-testid={`add-to-cart-${product.sku}`}
              onClick={() => addItem(product)}
              disabled={product.stock <= 0}
              className="bg-forest hover:bg-forest-dark disabled:bg-stone-300 text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm"
            >
              {product.stock > 0 ? "Sepete Ekle" : "Tükendi"}
            </button>
            {product.stock > 0 && (
              <button
                data-testid={`quick-buy-${product.sku}`}
                onClick={() => { addItem(product); navigate("/odeme"); }}
                className="flex items-center gap-1 text-[11px] font-bold text-gold-dark hover:text-forest transition-colors"
              >
                <Zap className="w-3 h-3" /> Hızlı Al
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
